import React, { useState, useMemo, useCallback } from "react";
import "./Pubblicazioni.scss";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../Components/Context/AppContext.jsx";
import { toast } from "react-toastify";
import DataTableSubHeader from "../../Components/DataTableSubHeader/DataTableSubHeader.jsx";
import ItemsTable from "../../Components/ItemsTable/ItemsTable.jsx";
import { SubHeaderMobileOption } from "../../Components/SubHeaderMobileOption/SubHeaderMobileOption.jsx";
import { EyeIcon, DeleteIcon } from "../../assets/icons/index.js";
import { subHeaderRightNavOptions } from "../../Components/Pubblicazioni/subHeaderOptions.js";
import datastructure from "../../constants/dataStructure/biblografia.json";
import { deleteBibliografia } from "../../api/bibliografia.js";
import Spinner from "../../Components/ProgressBar/Spinner.js";
import userHasPermission from "../../helpers/formTools/userHasPermission/index.js";
import ReactModal from "../../Components/ReactModal/ReactModal.jsx";
import FilterModal from "../../Components/FilterModal/FilterModal.jsx";
import useFetchData from "../../hooks/useFetchData.js";
import { getBibliografia } from "../../api/bibliografia.js";
import PubblicazioneCoverCell from "../../Components/Pubblicazioni/PubblicazioneCoverCell/PubblicazioneCoverCell";
import {
  getCoverFromAllegati,
  normalizeAllegati,
} from "../../Components/Pubblicazioni/helpers/allegatiHelpers";

// Breadcrumbs data for navigation
const breadcrumbsData = [
  { id: 1, label: "Dati Archeologici", iconUrl: true },
  { id: 2, label: "Pubblicazioni", iconUrl: true },
  { id: 3, label: "Elenco pubblicazioni", iconUrl: "" },
];

const headers = [
  { key: "actions", label: "", width: "100px", sortable: false },
  {
    key: "copertina",
    label: "Copertina",
    width: "80px",
    sortable: false,
  },
  {
    key: "abbreviazione",
    label: "Abbreviazione",
    width: "200px",
    sortable: false,
  },
  { key: "titolo", label: "Titolo", width: "400px", sortable: false },
  {
    key: "tipoDocumento",
    label: "Tipo di documento",
    width: "300px",
    sortable: false,
  },
  {
    key: "repertiInteressati",
    label: "Reperti interessati",
    width: "150px",
    sortable: false,
  },
];

const filterValues = [
  { label: "Abbreviazione", key: "abbreviazione" },
  { label: "Titolo", key: "titolo" },
  { label: "Tipo di documento", key: "tipoDocumento" },
  { label: "Anno di pubblicazione", key: "annoPubblicazione" }
];

function Pubblicazioni() {
  const navigate = useNavigate();
  const {
    setBibliografiaFilter,
    bibliografiaFilter,
    setBibliografiaId,
    userProfile,
  } = useAppContext();

  const [recordFilterModalOpen, setRecordFilterModalOpen] = useState(false);
  const [isMenuActive, setMenuActive] = useState(false);
  const [filterValue, setFilterValue] = useState({});
  const organization = JSON.parse(localStorage.getItem("organization"));
  const userCanWrite = userHasPermission({
    userProfile,
    resource: "BIBLIOGRAFIA",
    permission: "WRITE",
    organization: null
  });
  const userCanDelete = userHasPermission({
    userProfile,
    resource: "BIBLIOGRAFIA",
    permission: "DELETE",
    organization: null
  });


  const customFilter = useMemo(
    () => [...bibliografiaFilter],
    [bibliografiaFilter]
  );

  const {
    data,
    isLoading,
    currentPage,
    recordsPerPage,
    totalRecords,
    totalPages,
    sortDirection,
    setCurrentPage,
    setRecordsPerPage,
    setSortDirection,
  } = useFetchData(getBibliografia, customFilter);

  // Toggle menu active state
  const toggleMenuActive = () => setMenuActive(!isMenuActive);

  const removeBibliografia = async (id) => {
    try {
      await deleteBibliografia(id);
      toast.success("Pubblicazione eliminata con successo!");
      setTimeout(() => {
        window.location.reload();
      }, 20);
    } catch (error) {
      console.log("remove-biblografia-->>", error);
      toast.error("Eliminazione della pubblicazione fallita!");
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRecordsPerPageChange = (newRecordsPerPage) => {
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1); // Reset to first page when records per page changes
  };

  async function handleCallBackSort(key) {
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    setBibliografiaFilter([{ key: "sort", value: `${key},${sortDirection}` }]);
  }

  const closeRecordFilterModal = useCallback(() => {
    setRecordFilterModalOpen(false);
  }, []);

  const openRecordFilterModal = useCallback(() => {
    setRecordFilterModalOpen(true);
  }, []);

  const handleCallBackFilter = useCallback(
    (data) => {
      const filter = data
        .map((item) => ({
          key: filterValues.find((f) => f.label === item.label)?.key,
          value: item.value?.trim() ?? "",
        }))
        .filter((item) => item.key && item.value);
      setBibliografiaFilter(filter);
      setCurrentPage(1);
      return true;
    },
    [setBibliografiaFilter, setCurrentPage]
  );

  const handleReset = useCallback(() => {
    setBibliografiaFilter([]);
    setFilterValue({});
    setCurrentPage(1);
  }, [setBibliografiaFilter, setCurrentPage]);

  const convertedData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    let mappedData = []; // Initialize the array to store the converted data

    data.forEach((result) => {
      let jsonObject = { id: { value: result.id }, actions: { value: [] } };
      const cover = getCoverFromAllegati(normalizeAllegati(result.allegati));
      const repertiInteressati = result.reperti?.length > 0 ? result.reperti.length : "-";

      // Convert row attributes in the right format base on a key and a value to be displayed
      for (const key in result) {
        if (result.hasOwnProperty(key)) {
          if (key === "allegati") continue;

          let value;
          if (datastructure[key] && datastructure[key].type === "object") {
            value = result[key][datastructure[key].objectName];
          } else {
            value =
              result[key] !== undefined && result[key] !== null
                ? result[key].toString()
                : "";
          }
          jsonObject[key] = {
            value,
          };
        }
      }

      // Identifies the ID of the record
      const Id = jsonObject.id.value.replace(/^"|"$/g, "");

      jsonObject.copertina = {
        value: cover?.id || "",
        component: (
          <PubblicazioneCoverCell
            coverId={cover?.id}
            alt={cover?.name || result.titolo || "Copertina pubblicazione"}
          />
        ),
      };

      jsonObject.repertiInteressati = {
        value: repertiInteressati,
      };

      // Add the actions to the row
      jsonObject.actions = {
        value: [],
        component: (
          <>
            <div
              onClick={() => {
                navigate("/pubblicazioni/view");
                setBibliografiaId(Id);
              }}
              className="row-icon"
              style={{ cursor: "pointer" }}
            >
              <EyeIcon fillColor="var(--archeo-color)" />
            </div>
            {userCanDelete && (
              <div
                onClick={() => removeBibliografia(Id)}
                style={{ cursor: "pointer" }}
              >
                <DeleteIcon fillColor="var(--archeo-color)" />
              </div>
            )}
          </>
        ),
      };
      mappedData.push(jsonObject);
    });

    return mappedData;
  }, [data, userProfile, setBibliografiaId, navigate]);

  return (
    <div className="container">
      <main className="page-main-content">
        <DataTableSubHeader
          title="PUBBLICAZIONI"
          breadcrumbsData={breadcrumbsData}
          subHeaderRightNavOptions={subHeaderRightNavOptions(
            userCanWrite,
            convertedData,
            navigate,
            openRecordFilterModal
          )}
          isMenuActive={isMenuActive}
          setMenuActive={toggleMenuActive}
        />
        <ReactModal
          isOpen={recordFilterModalOpen}
          onClose={closeRecordFilterModal}
        >
          <FilterModal
            closeModal={closeRecordFilterModal}
            filterValues={filterValues}
            setFilterValue={setFilterValue}
            callBackFilter={handleCallBackFilter}
            onReset={handleReset}
          />
        </ReactModal>
        {isLoading ? (
          <Spinner />
        ) : (
          <ItemsTable
            headers={headers}
            data={convertedData}
            filterValues={filterValues}
            totalRecords={totalRecords}
            currentPage={currentPage}
            recordsPerPage={recordsPerPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onRecordsPerPageChange={handleRecordsPerPageChange}
            callBackSort={handleCallBackSort}
            userCanRead={true}
          />
        )}
      </main>
      {isMenuActive && (
        <SubHeaderMobileOption
          components={
            subHeaderRightNavOptions(
              userCanWrite,
              convertedData,
              navigate,
              openRecordFilterModal
            ).components
          }
          icons={
            subHeaderRightNavOptions(userCanWrite, convertedData, navigate)
              .icons
          }
          onCrossClick={() => setMenuActive(false)}
          title="Options"
        />
      )}
    </div>
  );
}

export default Pubblicazioni;
